/**
 * useCarHeartbeat — drives the REAL "X watching now" presence signal.
 *
 * Iter 69 — every open CarDetail tab posts a heartbeat every ~25s to
 * `/api/cars/{id}/heartbeat`. The backend keeps a TTL-backed presence
 * collection and counts distinct heartbeats in the last 60s as the
 * "viewers_now" for that car. Hook handles:
 *   · Stable per-tab session id (sessionStorage, regenerated per tab).
 *   · Pings on mount, then every `intervalMs`.
 *   · Skips while the document is hidden (renters in background tabs
 *     drop off in under a minute via the server's 60s freshness window).
 *   · Sends a final beacon when the tab unloads so the count drops
 *     immediately (otherwise it lingers for up to 60s after they leave).
 *   · No-op when `carId` is null — convenient for `useCarHeartbeat(car?.id)`
 *     during the initial loading state.
 */
import { useEffect } from "react";
import api from "@/lib/api";

// `sessionStorage` is per-tab → opening two tabs of the same browser
// counts as 2 viewers, which matches the "concurrent attention" intent.
function getSessionId() {
  try {
    const KEY = "rentalcar.viewerSession";
    let sid = sessionStorage.getItem(KEY);
    if (!sid) {
      sid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
      sessionStorage.setItem(KEY, sid);
    }
    return sid;
  } catch {
    // Privacy-mode browsers may block sessionStorage — fall back to an
    // in-memory id for the life of the component.
    return `mem-${Math.random().toString(36).slice(2)}`;
  }
}

export default function useCarHeartbeat(carId, { intervalMs = 25000 } = {}) {
  useEffect(() => {
    if (!carId) return undefined;
    const sessionId = getSessionId();
    let timerId = null;
    let cancelled = false;

    const ping = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        await api.post(`/cars/${carId}/heartbeat`, { session_id: sessionId });
      } catch {
        // Swallow — presence is best-effort, never fail the UI for it.
      }
    };

    ping();  // immediate ping so the badge updates on the first batch fetch
    timerId = setInterval(ping, intervalMs);

    // Re-ping when the tab becomes visible again so the count picks back up
    // quickly after the renter returns from a background tab.
    const onVisibility = () => { if (!document.hidden) ping(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(timerId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [carId, intervalMs]);
}
